import { useEffect, useState } from 'react'
import type { CSSProperties } from 'react'
import { SHEET_TEMPLATES } from '../core/templates'
import { useStore } from '../core/store'

export default function TemplateDialog({
  onClose,
  onApplied,
}: {
  onClose: () => void
  onApplied: (message: string) => void
}) {
  const [selectedId, setSelectedId] = useState(SHEET_TEMPLATES[0].id)
  const [mode, setMode] = useState<'new' | 'replace'>('new')
  const applyTemplate = useStore((s) => s.applyTemplate)
  const currentName = useStore((s) => s.activeSheet().name)
  const selected = SHEET_TEMPLATES.find((t) => t.id === selectedId) ?? SHEET_TEMPLATES[0]

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const apply = () => {
    if (
      mode === 'replace' &&
      !window.confirm(`"${currentName}"의 기존 내용과 서식을 템플릿으로 덮어쓸까요?\n적용 후 Ctrl+Z로 되돌릴 수 있습니다.`)
    )
      return
    const sheet = applyTemplate(selected.create(), mode)
    onApplied(
      mode === 'new'
        ? `"${sheet.name}" 템플릿을 새 탭에 만들고 이동했습니다.`
        : `"${sheet.name}" 탭에 ${selected.name} 템플릿을 적용했습니다.`
    )
    onClose()
  }

  return (
    <div className="modal-backdrop" onMouseDown={onClose}>
      <div className="modal template-modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          ✨ 템플릿
          <button className="modal-x" onClick={onClose} title="닫기">
            ✕
          </button>
        </div>

        <div className="modal-body">
          <p className="template-intro">
            색상·서식·병합·열 너비와 계산 수식이 포함된 실무형 시트를 바로 만듭니다.
          </p>
          <div className="template-grid" role="listbox" aria-label="템플릿 목록">
            {SHEET_TEMPLATES.map((template) => (
              <button
                key={template.id}
                className={`template-card${selectedId === template.id ? ' selected' : ''}`}
                onClick={() => setSelectedId(template.id)}
                role="option"
                aria-selected={selectedId === template.id}
              >
                <div className="template-preview" style={{ '--template-accent': template.accent } as CSSProperties}>
                  <div className="template-preview-title">{template.icon}</div>
                  <div className="template-preview-kpis"><i /><i /><i /></div>
                  <div className="template-preview-table"><i /><i /><i /><i /></div>
                </div>
                <span className="template-category">{template.category}</span>
                <strong>{template.name}</strong>
                <small>{template.description}</small>
              </button>
            ))}
          </div>

          <fieldset className="template-mode">
            <legend>적용 위치</legend>
            <label className={mode === 'new' ? 'selected' : ''}>
              <input type="radio" name="template-mode" checked={mode === 'new'} onChange={() => setMode('new')} />
              <span>
                <b>새 탭에서 생성</b>
                <small>기존 시트를 유지하고 새 탭으로 바로 이동합니다.</small>
              </span>
              <em>기본</em>
            </label>
            <label className={mode === 'replace' ? 'selected' : ''}>
              <input
                type="radio"
                name="template-mode"
                checked={mode === 'replace'}
                onChange={() => setMode('replace')}
              />
              <span>
                <b>현재 탭 덮어쓰기</b>
                <small>현재 “{currentName}”의 내용과 서식을 교체합니다.</small>
              </span>
            </label>
          </fieldset>
        </div>

        <div className="modal-foot">
          <button className="btn" onClick={onClose}>취소</button>
          <button className="btn primary" onClick={apply}>
            {selected.icon} {selected.name} 적용
          </button>
        </div>
      </div>
    </div>
  )
}
